import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import type {HighlightRange} from './annotationUtils';
import {mergeRanges} from './annotationUtils';

interface AnnotationModeState {
  isAnnotationModeOn: boolean;
  highlightsByQuestionId: Record<string, HighlightRange[]>;
}

interface AnnotationModeActions {
  toggleAnnotationMode: () => void;
  addHighlight: (questionId: string, range: HighlightRange) => void;
  reset: () => void;
}

const initialState: AnnotationModeState = {
  isAnnotationModeOn: false,
  highlightsByQuestionId: {}
};

export const useAnnotationModeStore = create<AnnotationModeState & AnnotationModeActions>()(
  devtools(
    (set) => ({
      ...initialState,

      toggleAnnotationMode: () => {
        set(
          (state) => ({isAnnotationModeOn: !state.isAnnotationModeOn}),
          false,
          'toggleAnnotationMode'
        );
      },

      addHighlight: (questionId: string, range: HighlightRange) => {
        set(
          (state) => ({
            highlightsByQuestionId: {
              ...state.highlightsByQuestionId,
              [questionId]: mergeRanges(state.highlightsByQuestionId[questionId] ?? [], range)
            }
          }),
          false,
          'addHighlight'
        );
      },

      reset: () => {
        set(initialState, false, 'reset');
      }
    }),
    {name: 'AnnotationModeStore'}
  )
);

/**
 * Hook that resets the annotation mode store on mount and unmount.
 * Call this at the practice-session level (e.g. AssignmentPracticeView), not
 * inside the per-question subtree — PracticeView remounts on every question
 * navigation and the mode must stay on across questions.
 */
export function useAnnotationModeReset() {
  const reset = useAnnotationModeStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}
